import { useCallback, useEffect, useState } from 'react'

import api from '@/services/api'
import { formatWhen } from './newsSignOff'
import { errorDetail } from './totpClient'

type Policy = {
  required: boolean
  changedBy: string | null
  changedAt: string | null
  note: string
  // No authenticator and no passkey: asked to set up an authenticator at sign-in.
  unenrolled_permanent_accounts: number
  // A passkey and no authenticator: covered, and signing in must use the passkey.
  passkey_only_permanent_accounts?: number
}

function people(n: number): string {
  return n === 1 ? '1 person' : `${n} people`
}

/**
 * Whether two-factor sign-in is mandatory for permanent accounts. Default off.
 *
 * Whether to require it is the client's decision, so switching it on records
 * whose decision it is (who, where, when) and the server audits every change.
 * Switching it on locks nobody out: anyone with neither an authenticator nor a
 * passkey is taken through set-up at their next sign-in, and anyone who holds
 * a passkey signs in with it (a password alone is no longer accepted for them).
 * Super admin only; the server enforces that, and the page only offers the card
 * to one.
 */
export default function TwoFactorPolicyCard() {
  const [policy, setPolicy] = useState<Policy | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await api.get<Policy>('/settings/two-factor-policy')
      setPolicy(res.data)
      setLoadFailed(false)
    } catch {
      setLoadFailed(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const save = async (required: boolean) => {
    setBusy(true)
    setError(null)
    try {
      const res = await api.put<Policy>('/settings/two-factor-policy', {
        required,
        note: required ? note.trim() : '',
      })
      setPolicy(res.data)
      setNote('')
      setConfirming(false)
    } catch (err) {
      setError(errorDetail(err, 'The policy could not be saved.'))
    } finally {
      setBusy(false)
    }
  }

  if (loadFailed && !policy) {
    return (
      <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
        Could not read the two-factor policy.
      </div>
    )
  }
  if (!policy) return null

  const waiting = policy.unenrolled_permanent_accounts
  const passkeyOnly = policy.passkey_only_permanent_accounts ?? 0

  return (
    <div className="max-w-xl space-y-2 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.12em] text-stone-500">Two-factor sign-in policy</div>
      {policy.required ? (
        <>
          <p className="text-[11px] text-emerald-300/90">Two-factor sign-in is required.</p>
          <p className="text-[11px] text-stone-400">
            Decided by {policy.changedBy ?? 'an admin'}
            {formatWhen(policy.changedAt) ? ` on ${formatWhen(policy.changedAt)}` : ''}.
          </p>
          {policy.note ? <p className="text-[11px] text-stone-400">Reference: {policy.note}</p> : null}
          <p className="text-[10px] text-stone-500">
            Permanent accounts with neither an authenticator nor a passkey are taken through set-up at sign-in; an
            account with a passkey signs in with it. Trial licenses keep their own setting.
          </p>
          <button
            type="button"
            className="text-[11px] text-stone-300 underline-offset-2 hover:text-stone-100 hover:underline disabled:opacity-40"
            disabled={busy}
            onClick={() => void save(false)}
          >
            Stop requiring
          </button>
        </>
      ) : (
        <>
          <p className="text-[11px] text-amber-200/90">Two-factor sign-in is not required.</p>
          <p className="text-[10px] text-stone-500">
            {waiting === 1
              ? '1 active permanent account has no authenticator or passkey yet.'
              : `${waiting} active permanent accounts have no authenticator or passkey yet.`}
          </p>
          {passkeyOnly > 0 ? (
            <p className="text-[10px] text-stone-500">
              {passkeyOnly === 1
                ? '1 more has a passkey and no authenticator: it will have to sign in with the passkey.'
                : `${passkeyOnly} more have a passkey and no authenticator: they will have to sign in with the passkey.`}
            </p>
          ) : null}
          {confirming ? (
            <div className="space-y-2">
              <p className="text-[11px] text-stone-300">
                {waiting === 0
                  ? 'Everyone already has an authenticator or a passkey.'
                  : `${people(waiting)} will be asked to set up an authenticator at their next sign-in.`}{' '}
                {passkeyOnly > 0
                  ? `${people(passkeyOnly)} will have to sign in with their passkey; a password alone will be refused. `
                  : ''}
                Sessions already open continue until they expire.
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="btn-primary text-[12px] disabled:opacity-50"
                  disabled={busy}
                  onClick={() => void save(true)}
                >
                  Yes, require it
                </button>
                <button
                  type="button"
                  className="text-[11px] text-stone-500 hover:text-stone-300"
                  disabled={busy}
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <label className="block text-[10px] text-stone-500">
                Whose decision is this? (who, where, when)
                <input
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="e.g. email from the client, 20 Oct 2026"
                  className="mt-1 w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-[11px] text-stone-200 placeholder:text-stone-600 focus:border-amber-300/35 focus:outline-none"
                />
              </label>
              <button
                type="button"
                className="btn-primary text-[12px] disabled:opacity-50"
                disabled={busy || !note.trim()}
                onClick={() => setConfirming(true)}
              >
                Require two-factor sign-in
              </button>
            </>
          )}
        </>
      )}
      {error ? <p className="text-[11px] text-red-300">{error}</p> : null}
    </div>
  )
}
